import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import S from './Widget.module.scss';
import WidgetHeaderControls from './WidgetHeaderControls';
import BibliothequePlotly from '../../Graphe/AffichageGraphe/BibliothequePlotly/BibliothequePlotly';

const WidgetGraphe = ({
  graphesFavoris = [],
  onGrapheClick,
  headerContent = null,
  showHeader = true,
}) => {
  const hasGraphes = graphesFavoris && graphesFavoris.length > 0;
  const [selectedGrapheIndex, setSelectedGrapheIndex] = useState('0');
  const graphLength = graphesFavoris?.length || 0;

  useEffect(() => {
    if (!hasGraphes) {
      setSelectedGrapheIndex('0');
      return;
    }

    const currentIndex = Number(selectedGrapheIndex);
    if (currentIndex >= graphLength) {
      setSelectedGrapheIndex('0');
    }
  }, [graphLength, hasGraphes, selectedGrapheIndex]);

  const grapheOptions = useMemo(() => {
    if (!hasGraphes || graphLength < 2) {
      return [];
    }

    return graphesFavoris.map((graphe, index) => ({
      value: String(index),
      label: graphe.graph_name || `${checkMot('graphe')} ${index + 1}`,
    }));
  }, [graphLength, graphesFavoris, hasGraphes]);

  const currentGraphe = hasGraphes
    ? graphesFavoris[Number(selectedGrapheIndex)] || graphesFavoris[0]
    : null;

  // Preparar dados para BibliothequePlotly
  const plotlyData = useMemo(() => {
    if (!currentGraphe) return [];

    // Se dados já estão no formato Plotly com array de séries
    if (currentGraphe.data && Array.isArray(currentGraphe.data)) {
      return currentGraphe.data.map((series, index) => {
        // Converter timestamps Unix em datas legíveis para Plotly
        const xData =
          series.x && Array.isArray(series.x)
            ? series.x.map((timestamp) => new Date(timestamp * 1000))
            : [];

        const colors = ['#000000', '#FF6B6B', '#4ECDC4'];

        return {
          x: xData,
          y: series.y || [],
          type: series.type || 'scatter',
          mode: series.mode || 'lines',
          name: series.sensor_name || `Série ${index + 1}`,
          line: {
            color: series.color || colors[index % colors.length],
            dash: series.line?.dash || 'solid',
            shape: series.line?.shape || 'linear',
          },
          hovertemplate:
            '<b>%{fullData.name}</b><br>%{x|%d-%m-%Y %H:%M}<br>%{y:.1f}°C<extra></extra>',
        };
      });
    }

    // Fallback com dados de exemplo
    const topBars = [72, 58, 80, 64, 92, 65, 78];
    const bottomBars = [55, 42, 63, 51, 70, 57, 48, 62, 59, 40];

    return [
      {
        x: Array.from({ length: topBars.length }, (_, i) => i),
        y: topBars,
        type: 'bar',
        name: 'Dados Superior',
      },
      {
        x: Array.from({ length: bottomBars.length }, (_, i) => i),
        y: bottomBars,
        type: 'bar',
        name: 'Dados Inferior',
      },
    ];
  }, [currentGraphe]);

  const plotlyLayout = useMemo(() => {
    const config = currentGraphe?.config || {};

    return {
      title: {
        text: config.title || currentGraphe?.graph_name || 'Gráfico',
        subtitle: { text: config.subtitle || '' },
      },
      showlegend: false,
      hovermode: 'x unified',
      xaxis: {
        title: config.xaxis?.title || 'X',
        type: config.xaxis?.type || 'date',
        rangeslider: { visible: false },
      },
      yaxis: {
        title: config.yaxis?.title || 'Y',
        type: config.yaxis?.type || 'linear',
      },
      margin: { t: 60, r: 40, b: 40, l: 60 },
    };
  }, [currentGraphe]);

  const resolvedHeader =
    headerContent ||
    (showHeader ? (
      <WidgetHeaderControls
        titleKey="graphe"
        selectOptions={grapheOptions}
        selectedValue={selectedGrapheIndex}
        onSelectChange={setSelectedGrapheIndex}
      />
    ) : null);

  return (
    <div className={[S.widget, S.widgetMedium].join(' ')}>
      {resolvedHeader}
      <div className={S.widgetContent}>
        {hasGraphes && currentGraphe ? (
          <div className={S.grapheContainer}>
            <BibliothequePlotly
              data={plotlyData}
              layout={plotlyLayout}
              dimensionWidth={
                window.innerWidth <= 1024 ? window.innerWidth - 100 : 400
              }
              dimensionHeight={window.innerWidth <= 1024 ? 300 : 250}
              minimaliste={false}
            />
            {currentGraphe?.graph_id && (
              <Link
                to={`/graphe/${currentGraphe.graph_id}`}
                className={S.widgetLink}
                onClick={() => onGrapheClick && onGrapheClick(currentGraphe)}
              >
                {checkMot('voir_plus')}
              </Link>
            )}
          </div>
        ) : (
          <div className={S.emptyState}>
            <p className={S.emptyText}>{checkMot('aucun_graphe_favori')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetGraphe;
